import { useEffect, useState, useRef } from 'react';
import { Input, Checkbox, Button, message, Divider } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import { DeleteOutlined } from '@ant-design/icons';
import { Loading } from 'src/DigitalProtocolModule/components';
import {
	getStudyAnalysisSetList
} from 'src/DigitalProtocolModule/utils/ajax-proxy';
import { ConfirmModal } from '..'
import styles from './index.module.scss';
import './index.scss';

export function AnalysisSet({ trial_uuid,populationList, isReadOnly, onAnalysisSetChange }: { trial_uuid: string,populationList:  Digital_Protocol_StudyPopulation.PopulationList[], isReadOnly?: boolean, onAnalysisSetChange: (newAnalysisSet: Digital_Protocol_StudyPopulation.NewAnalysisSet[]) => void }) {

	const [list, setList] = useState<Digital_Protocol_StudyPopulation.AnalysisSet[]>([]);
	const [includesSelectedList, setIncludesSelectedList] = useState([]);
	const [newAnalysisSets, setNewAnalysisSets] = useState<{ name: string, description: string }[]>([]);  
	const [isLoading, setIsLoading] = useState(false);
	const [descriptions, setDescriptions] = useState<{ [key: string]: string }>({});	
	const [selectedCount, setSelectedCount] = useState(0);
  const newAnalysisInputRef = useRef<Input>(null);
  const [isShowDelConfim, setIsShowDelConfim] = useState(false);
	const [deleteIndex, setDeleteIndex] = useState<number | null>(null); // Track which analysis set to delete

  const fetchStudyPopulationAnalysisSetList = async () => {
    try {
      setIsLoading(true);
      const res: Digital_Protocol_StudyPopulation.AnalysisSet[] = await getStudyAnalysisSetList(
        trial_uuid
      );
      setList(res || []);			
    } catch (error) {
			setIsLoading(false);
      message.error('Failed to fetch the analysissets')
    } finally {
      setIsLoading(false);
    }
  }

	useEffect(()=>{
		fetchStudyPopulationAnalysisSetList();
	},[]);

	useEffect(() => {
		// Extract analysis sets from the populationList
		const extractedAnalysisSets = populationList.map(population => population.analysis_set).flat();

		if (extractedAnalysisSets.length > 0) {
			const initialDescriptions: { [key: string]: string } = {};
			const customAnalysisSets: { name: string; description: string }[] = [];
			const defaultAnalysisSets: Digital_Protocol_StudyPopulation.AnalysisSet[] = [];

			// Iterate over the extracted analysis sets
			extractedAnalysisSets.forEach((set) => {
				initialDescriptions[set.analysis_set_name] = set.analysis_set_description || '';
				if (set.user_defined) {
					customAnalysisSets.push({ name: set.analysis_set_name, description: set.analysis_set_description });
				} else if (list.some((item) => item.analysis_set_name === set.analysis_set_name)) {
					defaultAnalysisSets.push({
						analysis_set_name: set.analysis_set_name,
						analysis_set_description: set.analysis_set_description || '',
					});
				}
			});

			// Update state with the categorized analysis sets
			setDescriptions(initialDescriptions);
			setIncludesSelectedList(defaultAnalysisSets);
			setNewAnalysisSets(customAnalysisSets);
			setSelectedCount(defaultAnalysisSets.length);
		}
	}, [populationList, list]); // Ensure 'list' is included in the dependency array if it's used within the effect

	const onChange = (checkedValues: CheckboxValueType[]) => {		
		const includesSelectedListItems = list.filter((item) =>
			checkedValues.includes(item.analysis_set_name)
		);
		setIncludesSelectedList(includesSelectedListItems);		
		setSelectedCount(checkedValues.length); // Update the selected count    
	};

	const handleDescriptionChange = (name: string, value: string) => {
    setDescriptions({
      ...descriptions,
      [name]: value
    });
  };

	const validateNewAnalysisSet = (name: string): boolean => {
		if (!name.trim()) {
			message.error('Analysis Set Name cannot be empty');
			return false;
		}
		// Add more validation rules as needed
		return true;
	};

	const addNewAnalysisSet = () => {
		// Check the last added set for validation
		if (newAnalysisSets.length > 0) {
			const lastSet = newAnalysisSets[newAnalysisSets.length - 1];
			if (!validateNewAnalysisSet(lastSet.name)) {
				return;
			}
		}

		// Create a new analysis set object with default values
		const newSet = { name: '', description: '' };

		// Update the state to include the new set
		setNewAnalysisSets((prevSets) => [...prevSets, newSet]);

    // Use ref to focus the new input (if needed)
    setTimeout(() => {
      if (newAnalysisInputRef.current) {
        newAnalysisInputRef.current.focus();
      }
    }, 0);
	};

	const handleNewAnalysisSetChange = (index: number, key: 'name' | 'description', value: string) => {  
    const updatedSets = [...newAnalysisSets];  
    updatedSets[index][key] = value;  		
    setNewAnalysisSets(updatedSets);  
  };  

  const confirmDeleteAnalysisSet = (index: number) => {
    setDeleteIndex(index);
    setIsShowDelConfim(true);
  };

  const deleteAnalysisSet = () => {
    if (deleteIndex !== null) {
      const updatedSets = newAnalysisSets.filter((_, i) => i !== deleteIndex);
      setNewAnalysisSets(updatedSets);
      setDeleteIndex(null);
    }
    setIsShowDelConfim(false);
  };

  useEffect(() => {
		// Filter to include only those sets with a name
	
    const analysisSet = [
      ...includesSelectedList.map(item => ({
        analysis_set_name: item.analysis_set_name,
        analysis_set_description: descriptions[item.analysis_set_name] || ''
      })),
	  ...newAnalysisSets.map(set => ({
        analysis_set_name: set.name,
        analysis_set_description: set.description
      }))
    ];    		
    onAnalysisSetChange(analysisSet); // Pass the updated analysis set to the parent
  }, [includesSelectedList, newAnalysisSets, descriptions]);


  return (
    <>
     <div className={styles.analysiscontainer}>
		 		<div className={styles.container1}>
					<span>Select Analysis Set{selectedCount > 0 ? ` (${selectedCount})` : ''}</span>  {/* Display selected count */}
					<div className={styles.analysisContentDiv}>
						<Loading isLoading={isLoading}>
							<div className={` ${styles.checkboxWrapper} checkbox-group`}>
								<Checkbox.Group									
									onChange={onChange}
									value={includesSelectedList.map(set => set.analysis_set_name)}
									disabled={isReadOnly}
								>
								{list.map((item) => (
									<div className={styles.checkboxItem} key={item.analysis_set_name}>
										<Checkbox value={item.analysis_set_name} >
											{item.analysis_set_name}
										</Checkbox>
									</div>
								))}
								</Checkbox.Group> 
							</div> 
						</Loading>
						<Button
							className={styles.secondaryBtn}
							key="save"
							type="text"
							disabled={isReadOnly}
							onClick={addNewAnalysisSet}						
						>
							+ New Analysis Set
						</Button>   
					</div>    
        </div>
        <div className={styles.container2}>
            Analysis Description
						{includesSelectedList.map((item) => (
							<div className={styles.analysisDiv} key={item.analysis_set_name}>
								<span>{item.analysis_set_name}</span>
								<TextArea 
									placeholder={`${item.analysis_set_name} description`}
									maxLength={500}
									value={descriptions[item.analysis_set_name] || ''}
									onChange={e => handleDescriptionChange(item.analysis_set_name, e.target.value)}
									readOnly={isReadOnly}
								/>
							</div>
						))}
						<Divider>Added</Divider>
						{newAnalysisSets.map((set, index) => (  
            <div className={styles.addedNewAnalysisDiv} key={index}>  
              <Input 
								ref={index === newAnalysisSets.length - 1 ? newAnalysisInputRef : null}
								className={styles.inputBox} 
                placeholder="Analysis Name *"  
                value={set.name}  
                onChange={e => handleNewAnalysisSetChange(index, 'name', e.target.value)}  
								maxLength={50}								
								readOnly={isReadOnly}
              />							
							<DeleteOutlined
								className={styles.delIcon}
								onClick={() => {
									!isReadOnly && confirmDeleteAnalysisSet(index)
								}}
							/>
              <TextArea 
								className={styles.addedNewAnalysisDiv} 
                placeholder='Analysis Description'  
                value={set.description}  
                onChange={e => handleNewAnalysisSetChange(index, 'description', e.target.value)}  
								maxLength={500}
								readOnly={isReadOnly}
              />  
            </div>  
          ))}  
        </div>
     </div>

			<ConfirmModal
				visible={isShowDelConfim}
				title={'Delete Analysis Set'}
				content={
					'Are you sure you want to delete this analysis set?'
				}
				handleCancel={() => setIsShowDelConfim(false)}
        handleConfirm={deleteAnalysisSet}
			/>
    </>
  )
}